import { useModal } from '@ebay/nice-modal-react';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';

import { type AvailabilityStatus } from '@/lib/api/livres/availabilityStatus';
import { useCheckoutStore } from '@/stores/useCheckoutStore';

import { InfoModal } from '../components/modals/InfoModal';
import { buildAvailabilityRefusalModal } from './buildAvailabilityRefusalModal';
import { partitionAvailability } from './partitionAvailability';

/**
 * Contrôle de dernière seconde avant le passage en caisse.
 *
 * Il connaît DEUX causes de refus, et leur applique DEUX gestes différents.
 * L'asymétrie est délibérée, c'est la décision structurante de #433 :
 *
 * - INDISPONIBILITÉ CATALOGUE (`!disponible` : Vendu, ordered, indisponible, ou
 *   « Réservé » pour quelqu'un d'autre) ⇒ modale ET retrait du panier. L'état
 *   est définitif : garder l'article ne ferait qu'entretenir l'espoir.
 *
 * - VERROU tenu par un tiers (`retenu && !pour_moi && !isLockedByCurrent`)
 *   ⇒ modale `ModalOeuvreRetenue` ET refus, mais l'article RESTE dans le
 *   panier — d'où une modale à lui, qui ne promet aucun retrait. Le verrou expire ;
 *   surtout, la doctrine « je ne sais pas ⇒ retenu » de `fetchEtatVerrous` ne
 *   se justifie que par « ça ne coûte qu'un bouton masqué ». Cet argument NE
 *   TIENT PAS pour un geste destructif : une panne de trois secondes de
 *   l'endpoint viderait des paniers réels, et le client ne retrouverait pas son
 *   article une fois la panne finie.
 *
 * - PANNE DU CONTRÔLE lui-même (#475 : fetch qui rejette, `res.json()` qui lève
 *   sur la page d'erreur HTML de Next) ⇒ modale ET refus, panier INTACT. Elle
 *   se range du côté du verrou, pas du catalogue, pour la même raison : on ne
 *   détruit rien sur une ignorance. Et surtout `checking` retombe QUOI QU'IL
 *   ARRIVE — sans quoi une coupure d'une seconde grise définitivement le bouton
 *   « Payer » chez les quatre appelants (`disabled={… || checking}`), et le
 *   client n'a plus aucun moyen de payer, ni la moindre explication.
 *
 * - LIGNE NON VÉRIFIÉE (#742 : `verification_impossible`, posé par
 *   `checkLivresAvailability` quand la lecture catalogue n'a pas rendu la
 *   ligne demandée) ⇒ même geste que la panne ci-dessus, dont c'est la version
 *   ligne par ligne : modale, refus, panier INTACT. Elle se disait auparavant
 *   `disponible: false`, donc RETRAIT — une lecture dégradée de trois secondes
 *   valait une vente perdue et un message faux (« vendue ») le 08/09.
 *   Ces lignes sont écartées AVANT la partition, jamais comptées dans
 *   `unavailable` ni dans `heldByOthers` (`partitionAvailability`).
 *
 * Dans les quatre cas `onSuccess` n'est pas appelé et la valeur rendue est `null`.
 */
export function useCartAvailabilityChecker() {
  const { cartItems, removeItemFromCart } = useCheckoutStore();
  const infoModal = useModal(InfoModal);
  const t = useTranslations();

  const [checking, setChecking] = useState(false);

  const checkAvailability = useCallback(
    // La FORME de la signature est gelée pour ses quatre appelants
    // (PayByCardButton, PayWithOtherMethod, Cart, PaypalButton) : un seul
    // argument, une valeur rendue. Le `any` d'origine est remplacé par un
    // générique, qui dit enfin la vérité : en cas de refus on ne rend PAS ce
    // que la suite aurait rendu, on rend `null`.
    async <T>(onSuccess: () => T): Promise<Awaited<T> | null> => {
      setChecking(true);

      // `panierToken` part AVEC CHAQUE LIGNE (#505). Sans lui, le pré-contrôle
      // ne présente aucun canal 3 et refuse le destinataire d'une offre — qui
      // n'a ni fiche ni session — sur ses propres pièces réservées, avec le
      // message « retenue par un autre client ». L'écran promettait alors une
      // réservation que la caisse allait refuser.
      const livreIds = cartItems.map((item) => ({
        id: item.id,
        privateLinkData: item.privateLinkData,
        panierToken: item.panierToken,
      }));

      // La MÊME modale que le `catch` réseau, pour la MÊME raison : « on n'a
      // pas su vérifier, ET ton panier est intact ». Un 200 sans `data` est une
      // panne du contrôle au même titre qu'un `fetch` qui rejette — on ne les
      // distingue pas côté client, et les deux doivent PARLER (#478). Une seule
      // définition : deux libellés pour le même fait finiraient par diverger.
      const montrerControleImpossible = (): void => {
        infoModal.show({
          // Bloc DÉDIÉ, et pas une clé empruntée : le message doit dire les
          // deux choses que le client a besoin d'entendre — on n'a pas pu
          // vérifier, ET son panier est intact. Une clé générique « erreur,
          // vérifiez votre panier » laisse croire à un retrait.
          title: t('Blocs.ModalControleImpossible.titre'),
          message: t('Blocs.ModalControleImpossible.message'),
          showCancel: false,
          confirmLabel: t('Blocs.ModalControleImpossible.bouton'),
        });
      };

      // Le `try` extérieur n'existe QUE pour son `finally` : il relâche l'état
      // de chargement sur tous les chemins, y compris celui d'une exception
      // venue de `onSuccess` — que l'on laisse volontairement remonter (voir
      // plus bas).
      try {
        let availability: AvailabilityStatus[] | null | undefined;

        // Le `catch` est serré sur les DEUX SEULES lignes qui parlent au
        // réseau. L'élargir à tout le corps avalerait les erreurs de
        // `onSuccess`, que `PaypalButton.createOrder` doit voir remonter :
        // les déguiser en `null` ferait démarrer un paiement sans commande en
        // face, dans un tunnel où la capture PRÉCÈDE la création de commande.
        try {
          const res = await fetch('/api/helpers/livres/check-availability', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(livreIds),
          });
          const corps: { data?: AvailabilityStatus[] | null } =
            await res.json();
          availability = corps.data;
        } catch {
          // Refus EXPLIQUÉ, et non état bloqué : sans cette modale le client
          // voit un bouton qui ne fait rien et n'apprend jamais pourquoi.
          // Aucun `removeItemFromCart` ici — c'est la décision structurante :
          // trois secondes de panne masquent un bouton, elles ne vident pas un
          // panier que le client ne saurait pas reconstituer.
          montrerControleImpossible();
          return null;
        }

        if (availability) {
          // Partition d'abord (#742), puis UNE modale qui porte tous les faits
          // constatés : voir `partitionAvailability` et
          // `buildAvailabilityRefusalModal`.
          const partition = partitionAvailability(availability);
          const { unverifiedLines, unavailable, heldByOthers } = partition;
          const modal = buildAvailabilityRefusalModal(partition, t);

          // LES PIÈCES PARTIES sont retirées — c'est le seul retrait
          // automatique du chantier, et il est justifié : le catalogue fait
          // foi, la pièce n'existe plus à la vente.
          unavailable.forEach((item) => removeItemFromCart(item.id));

          if (modal) {
            const { offerRemoval, ...modalProps } = modal;
            infoModal.show({
              ...modalProps,
              onConfirm: offerRemoval
                ? () => {
                    heldByOthers.forEach((item) => removeItemFromCart(item.id));
                  }
                : undefined,
            });
          }

          if (
            unavailable.length === 0 &&
            heldByOthers.length === 0 &&
            unverifiedLines.length === 0
          ) {
            // `await` puis retour d'une variable, plutôt qu'un `return await` que
            // la règle `@typescript-eslint/return-await` refuse ici (`T` n'est pas
            // forcément une promesse). L'attente reste voulue : elle résout le
            // générique en `Awaited<T>` et laisse remonter une erreur de la suite
            // au lieu de rendre une promesse rejetée en douce.
            const resultat = await onSuccess();
            return resultat;
          }
        } else {
          // #478 — 200 SANS `data`. La route a répondu, mais son corps ne porte
          // aucun verdict (`corps.data` absent). C'est une panne du contrôle,
          // pas un feu vert : sans cette branche, on retombait sur le
          // `return null` muet plus bas et le client revoyait un bouton
          // « Payer » qui ne faisait rien, sans un mot. Même modale, même refus,
          // panier INTACT — comme le `catch` réseau, dont ce cas est le jumeau.
          montrerControleImpossible();
        }

        return null;
      } finally {
        setChecking(false);
      }
    },
    [cartItems, infoModal, removeItemFromCart, t],
  );

  return { checkAvailability, checking };
}
